import { Capacitor } from "@capacitor/core";
import { Directory, Filesystem } from "@capacitor/filesystem";
import type { FileChatThread } from "./chatStore";
import { appendLog } from "./logger";
import { base64ToArrayBuffer } from "./incomingFile";

const CACHE_FOLDER = "nevisign-docs";
const INDEX_KEY = "nr-doc-cache-index";
const MAX_CACHED = 30;

export type CachedDocMeta = {
  /** Relative path under Directory.Data, e.g. nevisign-docs/foo.pdf */
  cachePath: string;
  fileName: string;
  byteLength: number;
  mimeType?: string;
  sourceUri?: string;
  updatedAt: string;
};

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunk = 0x8000;
  let binary = "";
  for (let i = 0; i < bytes.length; i += chunk) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(binary);
}

function safeFileName(name: string): string {
  const base = (name || "document").replace(/[^\w.\-ء-ی]+/g, "_").slice(0, 80);
  return base || "document";
}

function loadIndex(): CachedDocMeta[] {
  try {
    const raw = localStorage.getItem(INDEX_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CachedDocMeta[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveIndex(entries: CachedDocMeta[]): void {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(entries.slice(0, MAX_CACHED)));
  } catch {
    /* quota */
  }
}

function upsertIndex(meta: CachedDocMeta): void {
  const rest = loadIndex().filter(
    (e) =>
      e.cachePath !== meta.cachePath &&
      !(e.fileName === meta.fileName && e.byteLength === meta.byteLength)
  );
  rest.unshift(meta);
  saveIndex(rest);
}

export function findCachedDoc(
  fileName: string,
  byteLength?: number
): CachedDocMeta | null {
  const list = loadIndex();
  if (byteLength != null) {
    const exact = list.find(
      (e) => e.fileName === fileName && e.byteLength === byteLength
    );
    if (exact) return exact;
  }
  return list.find((e) => e.fileName === fileName) ?? null;
}

export function threadHasResolvableFile(thread: FileChatThread): boolean {
  if (thread.filePath || thread.fileUri) return true;
  return Boolean(findCachedDoc(thread.fileName, thread.byteLength));
}

/**
 * Persist a copy of the opened document under app files so chats can reopen
 * even after content:// grants expire or the picker provided no URI.
 */
export async function cacheOpenedDocument(
  fileName: string,
  buffer: ArrayBuffer,
  opts?: { mimeType?: string; sourceUri?: string }
): Promise<string | undefined> {
  if (!Capacitor.isNativePlatform()) return undefined;
  try {
    await Filesystem.mkdir({
      path: CACHE_FOLDER,
      directory: Directory.Data,
      recursive: true,
    }).catch(() => undefined);

    const stamp = `${Date.now().toString(36)}`;
    const cachePath = `${CACHE_FOLDER}/${stamp}-${safeFileName(fileName)}`;
    await Filesystem.writeFile({
      path: cachePath,
      data: arrayBufferToBase64(buffer),
      directory: Directory.Data,
    });

    let uri: string | undefined;
    try {
      const resolved = await Filesystem.getUri({
        path: cachePath,
        directory: Directory.Data,
      });
      uri = resolved.uri;
    } catch {
      /* optional */
    }

    upsertIndex({
      cachePath,
      fileName,
      byteLength: buffer.byteLength,
      mimeType: opts?.mimeType,
      sourceUri: opts?.sourceUri,
      updatedAt: new Date().toISOString(),
    });

    appendLog("info", "doc-cache", `cached ${fileName}`, {
      cachePath,
      bytes: buffer.byteLength,
      uri,
    });
    return cachePath;
  } catch (err) {
    appendLog(
      "warn",
      "doc-cache",
      "cache write failed",
      err instanceof Error ? err.message : err
    );
    return undefined;
  }
}

async function readFromCachePath(cachePath: string): Promise<ArrayBuffer | null> {
  try {
    const result = await Filesystem.readFile({
      path: cachePath,
      directory: Directory.Data,
    });
    if (typeof result.data !== "string" || !result.data) return null;
    return base64ToArrayBuffer(result.data);
  } catch {
    return null;
  }
}

async function readFromUri(uri: string): Promise<ArrayBuffer | null> {
  try {
    const result = await Filesystem.readFile({ path: uri });
    if (typeof result.data !== "string" || !result.data) return null;
    return base64ToArrayBuffer(result.data);
  } catch {
    return null;
  }
}

export type ResolvedDocument = {
  buffer: ArrayBuffer;
  fileName: string;
  mimeType?: string;
  /** Best durable URI / path for future reopen */
  fileUri?: string;
  filePath?: string;
};

/**
 * Re-link a chat thread to a still-available local document copy.
 * Order: cached app copy → stored filePath → original content:// URI →
 * filename+size index match.
 */
export async function resolveDocumentForThread(
  thread: FileChatThread
): Promise<ResolvedDocument | null> {
  const indexed = findCachedDoc(thread.fileName, thread.byteLength);
  const cachePath = thread.filePath || indexed?.cachePath;

  if (cachePath) {
    const buffer = await readFromCachePath(cachePath);
    if (buffer) {
      return {
        buffer,
        fileName: thread.fileName,
        mimeType: thread.mimeType || indexed?.mimeType,
        filePath: cachePath,
        fileUri: thread.fileUri,
      };
    }
  }

  const uri = thread.fileUri;
  if (uri) {
    const buffer = await readFromUri(uri);
    if (buffer) {
      // Refresh durable cache from still-readable URI
      const newCachePath = await cacheOpenedDocument(thread.fileName, buffer, {
        mimeType: thread.mimeType,
        sourceUri: uri,
      });
      return {
        buffer,
        fileName: thread.fileName,
        mimeType: thread.mimeType,
        fileUri: uri,
        filePath: newCachePath,
      };
    }
  }

  // Last resort: any cache entry with same name (size optional)
  const byName = findCachedDoc(thread.fileName);
  if (byName) {
    const buffer = await readFromCachePath(byName.cachePath);
    if (buffer) {
      return {
        buffer,
        fileName: thread.fileName,
        mimeType: thread.mimeType || byName.mimeType,
        filePath: byName.cachePath,
        fileUri: thread.fileUri || byName.sourceUri,
      };
    }
  }

  appendLog("warn", "doc-cache", "resolve failed", {
    fileName: thread.fileName,
    fileUri: thread.fileUri,
    filePath: thread.filePath,
    byteLength: thread.byteLength,
  });
  return null;
}
